import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import Modal from '../components/Modal';
import { 
  Package, 
  Plus, 
  Edit, 
  Trash2, 
  Search,
  Filter
} from 'lucide-react';

function Prodotti() {
  const [prodotti, setProdotti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategoria, setSelectedCategoria] = useState('');
  const [categorie, setCategorie] = useState([]);
  const [tipologie, setTipologie] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingProdotto, setEditingProdotto] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    tipologia: '',
    categoria: '',
    tipologie_ids: [],
    tipologia_default_id: null,
    nota_default: ''
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [prodottoToDelete, setProdottoToDelete] = useState(null);

  useEffect(() => {
    loadProdotti();
    loadCategorie();
    loadTipologie();
  }, []);

  const loadProdotti = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/prodotti', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setProdotti(response.data.data);
      }
    } catch (error) {
      console.error('Errore caricamento prodotti:', error);
      toast.error('Errore nel caricamento dei prodotti');
    } finally {
      setLoading(false);
    }
  };

  const loadCategorie = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/categorie', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success && response.data.data) {
        // La route pubblica restituisce già solo categorie attive con id, nome, descrizione
        // Estrai solo il nome
        const categorieNomi = response.data.data.map(cat => cat.nome);
        setCategorie(categorieNomi);
      } else {
        console.warn('Nessuna categoria trovata');
        setCategorie([]);
      }
    } catch (error) {
      console.error('Errore caricamento categorie:', error);
      toast.error('Errore nel caricamento categorie');
      setCategorie([]);
    }
  };

  const loadTipologie = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/tipologie', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success && response.data.data) {
        // La route pubblica restituisce già solo tipologie attive con id, nome, descrizione
        // Non serve filtrare per attivo, sono già filtrate
        setTipologie(response.data.data);
      } else {
        console.warn('Nessuna tipologia trovata');
        setTipologie([]);
      }
    } catch (error) {
      console.error('Errore caricamento tipologie:', error);
      toast.error('Errore nel caricamento tipologie');
      setTipologie([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nome || !formData.categoria) {
      toast.error('Nome e categoria sono obbligatori');
      return;
    }
    
    if (formData.tipologie_ids.length === 0 && !formData.tipologia) {
      toast.error('Devi selezionare almeno una tipologia');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      const payload = {
        nome: formData.nome,
        categoria: formData.categoria,
        tipologie_ids: formData.tipologie_ids.length > 0 ? formData.tipologie_ids : null,
        tipologia_default_id: formData.tipologia_default_id || (formData.tipologie_ids.length > 0 ? formData.tipologie_ids[0] : null),
        nota_default: formData.nota_default || null
      };
      
      // Se non ci sono tipologie_ids ma c'è vecchio campo tipologia, mantieni compatibilità
      if (!payload.tipologie_ids && formData.tipologia) {
        payload.tipologia = formData.tipologia;
      }
      
      if (editingProdotto) {
        // Aggiorna prodotto esistente
        const response = await axios.put(`/api/prodotti/${editingProdotto.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.success) {
          toast.success('Prodotto aggiornato con successo');
          loadProdotti();
        }
      } else {
        // Crea nuovo prodotto
        const response = await axios.post('/api/prodotti', payload, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.success) {
          toast.success('Prodotto creato con successo');
          loadProdotti();
        }
      }

      setShowForm(false);
      setEditingProdotto(null);
      setFormData({ nome: '', tipologia: '', categoria: '', tipologie_ids: [], tipologia_default_id: null, nota_default: '' });
    } catch (error) {
      console.error('Errore salvataggio prodotto:', error);
      toast.error(error.response?.data?.message || 'Errore nel salvataggio del prodotto');
    }
  };

  const handleTipologiaToggle = (tipologiaId) => {
    const current = formData.tipologie_ids;
    const newIds = current.includes(tipologiaId)
      ? current.filter(id => id !== tipologiaId)
      : [...current, tipologiaId];
    
    let newDefault = formData.tipologia_default_id;
    
    // Se si deseleziona la default, imposta la prima disponibile o null
    if (newDefault === tipologiaId && !newIds.includes(tipologiaId)) {
      newDefault = newIds.length > 0 ? newIds[0] : null;
    }
    
    // Se non c'è default ma ci sono tipologie, imposta la prima
    if (!newDefault && newIds.length > 0) {
      newDefault = newIds[0];
    }
    
    setFormData({ ...formData, tipologie_ids: newIds, tipologia_default_id: newDefault });
  };

  const handleSetDefault = (tipologiaId) => {
    setFormData({ ...formData, tipologia_default_id: tipologiaId });
  };

  const editProdotto = async (prodotto) => {
    try {
      const token = localStorage.getItem('token');
      
      // Ricarica il prodotto completo dal backend per avere le tipologie_disponibili aggiornate
      const prodottoRes = await axios.get('/api/prodotti', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (prodottoRes.data.success) {
        const prodottoCompleto = prodottoRes.data.data.find(p => p.id === prodotto.id);
        
        if (prodottoCompleto) {
          prodotto = prodottoCompleto; // Usa il prodotto completo con tipologie_disponibili
        }
      }
      
      // Se la categoria non è nella lista (disattivata), aggiungiamola temporaneamente
      const categoriaEsiste = categorie.includes(prodotto.categoria);
      
      if (!categoriaEsiste) {
        const allCatsRes = await axios.get('/api/categorie/admin/list', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (allCatsRes.data.success) {
          const catCorrente = allCatsRes.data.data.find(c => c.nome === prodotto.categoria);
          if (catCorrente) {
            setCategorie(prev => [...prev, catCorrente.nome]);
          }
        }
      }
      
      // Estrai tipologie_ids e tipologia_default_id dal prodotto
      // tipologie_disponibili può essere array di oggetti {id, nome} oppure array di stringhe
      const tipologieDisponibili = prodotto.tipologie_disponibili || [];
      const tipologieIds = tipologieDisponibili
        .filter(t => t && (t.id || typeof t === 'object'))
        .map(t => t.id || (typeof t === 'object' ? null : t))
        .filter(Boolean);
      
      const defaultId = prodotto.tipologia_default_id || (tipologieIds.length > 0 ? tipologieIds[0] : null);
      
      setEditingProdotto(prodotto);
      setFormData({
        nome: prodotto.nome,
        tipologia: prodotto.tipologia || prodotto.tipologia_default_nome || '',
        categoria: prodotto.categoria,
        tipologie_ids: tipologieIds.length > 0 ? tipologieIds : (prodotto.tipologia ? [] : []),
        tipologia_default_id: defaultId,
        nota_default: prodotto.nota_default || ''
      });
      setShowForm(true);
    } catch (error) {
      console.error('Errore caricamento prodotto per modifica:', error);
      toast.error('Errore nel caricamento del prodotto');
    }
  };

  const deleteProdotto = (prodottoId) => {
    const prodotto = prodotti.find(p => p.id === prodottoId);
    setProdottoToDelete(prodotto);
    setShowDeleteModal(true);
  };

  const executeDeleteProdotto = async () => {
    if (!prodottoToDelete) return;

    setShowDeleteModal(false);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`/api/prodotti/${prodottoToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success('Prodotto eliminato con successo');
        loadProdotti();
      }
    } catch (error) {
      console.error('Errore eliminazione prodotto:', error);
      toast.error('Errore nell\'eliminazione del prodotto');
    } finally {
      setProdottoToDelete(null);
    }
  };

  const filteredProdotti = prodotti.filter(prodotto => {
    const matchesSearch = prodotto.nome.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategoria = !selectedCategoria || prodotto.categoria === selectedCategoria;
    return matchesSearch && matchesCategoria;
  });

  const groupedProdotti = filteredProdotti.reduce((acc, prodotto) => {
    if (!acc[prodotto.categoria]) {
      acc[prodotto.categoria] = [];
    }
    acc[prodotto.categoria].push(prodotto);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Prodotti</h1>
          <p className="mt-1 text-sm text-gray-500">
            Gestisci il catalogo prodotti per gli ordini
          </p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="mt-4 sm:mt-0 btn btn-primary"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nuovo Prodotto
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Cerca prodotti..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Filter className="h-5 w-5 text-gray-400" />
            </div>
            <select
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              value={selectedCategoria}
              onChange={(e) => setSelectedCategoria(e.target.value)}
            >
              <option value="">Tutte le categorie</option>
              {categorie.map(categoria => (
                <option key={categoria} value={categoria}>
                  {categoria.replace('_', ' ').toUpperCase()}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Products List */}
      <div className="space-y-6">
        {Object.keys(groupedProdotti).length === 0 ? (
          <div className="text-center py-12">
            <Package className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Nessun prodotto trovato</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || selectedCategoria ? 'Prova a modificare i filtri di ricerca.' : 'Aggiungi il tuo primo prodotto per iniziare.'}
            </p>
          </div>
        ) : (
          Object.entries(groupedProdotti).map(([categoria, prodotti]) => (
            <div key={categoria} className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">
                  {categoria.replace('_', ' ').toUpperCase()} ({prodotti.length})
                </h2>
              </div>
              
              <div className="divide-y divide-gray-200">
                {prodotti.map((prodotto) => (
                  <div key={prodotto.id} className="p-6 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h3 className="text-sm font-medium text-gray-900">{prodotto.nome}</h3>
                        <div className="mt-1 flex flex-wrap items-center gap-1">
                          {(() => {
                            const tipologie = prodotto.tipologie_disponibili || [];
                            const defaultNome = prodotto.tipologia_default_nome || prodotto.tipologia;
                            
                            if (tipologie.length === 0 && prodotto.tipologia) {
                              // Fallback se non ci sono tipologie disponibili ma c'è il vecchio campo
                              return (
                                <span className="text-sm text-gray-500">{prodotto.tipologia}</span>
                              );
                            }
                            
                            if (tipologie.length === 0) {
                              return (
                                <span className="text-sm text-gray-400 italic">Nessuna tipologia</span>
                              );
                            }
                            
                            // Separa default e altre tipologie
                            const tipologiaDefault = tipologie.find(t => t.nome === defaultNome);
                            const altreTipologie = tipologie.filter(t => t.nome !== defaultNome);
                            
                            return (
                              <>
                                {tipologiaDefault && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 border border-blue-300">
                                    {tipologiaDefault.nome}
                                    <span className="ml-1 text-blue-600">●</span>
                                  </span>
                                )}
                                {altreTipologie.length > 0 && (
                                  <span className="text-sm text-gray-500">
                                    {altreTipologie.map(t => t.nome).join(', ')}
                                  </span>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => editProdotto(prodotto)}
                          className="btn btn-outline btn-sm"
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Modifica
                        </button>
                        <button
                          onClick={() => deleteProdotto(prodotto.id)}
                          className="btn btn-outline btn-sm text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Product Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowForm(false)} />
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900">
                      {editingProdotto ? 'Modifica Prodotto' : 'Nuovo Prodotto'}
                    </h3>
                    <button
                      type="button"
                      onClick={() => setShowForm(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      ×
                    </button>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Nome Prodotto
                      </label>
                      <input
                        type="text"
                        required
                        className="mt-1 input"
                        value={formData.nome}
                        onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                        placeholder="es. Pomodori"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tipologie Disponibili
                      </label>
                      <p className="text-xs text-gray-500 mb-2">
                        ✓ Seleziona una o più tipologie disponibili per questo prodotto (es. "kg", "confezione", "cartone")
                      </p>
                      {tipologie.length === 0 ? (
                        <div className="mt-1 border border-yellow-300 rounded-md p-3 bg-yellow-50">
                          <p className="text-sm text-yellow-800">
                            ⚠️ Nessuna tipologia disponibile. Vai su <strong>Categorie & Tipologie</strong> per crearne una.
                          </p>
                        </div>
                      ) : (
                        <div className="mt-1 space-y-2 max-h-48 overflow-y-auto border border-gray-300 rounded-md p-3 bg-white">
                          {tipologie.map(tipologia => {
                            const isSelected = formData.tipologie_ids.includes(tipologia.id);
                            const isDefault = formData.tipologia_default_id === tipologia.id;
                            return (
                              <div key={tipologia.id} className={`flex items-center justify-between p-2 rounded transition-colors ${
                                isSelected ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-50'
                              }`}>
                                <label className="flex items-center flex-1 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => handleTipologiaToggle(tipologia.id)}
                                    className="mr-3 h-4 w-4 text-blue-600 focus:ring-2 focus:ring-blue-500"
                                  />
                                  <span className={`text-sm ${isSelected ? 'font-medium text-gray-900' : 'text-gray-700'}`}>
                                    {tipologia.nome}
                                  </span>
                                  {tipologia.descrizione && (
                                    <span className="ml-2 text-xs text-gray-500">({tipologia.descrizione})</span>
                                  )}
                                </label>
                                {isSelected && (
                                  <button
                                    type="button"
                                    onClick={() => handleSetDefault(tipologia.id)}
                                    className={`ml-2 px-2 py-1 text-xs rounded font-medium ${
                                      isDefault 
                                        ? 'bg-blue-600 text-white' 
                                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                    }`}
                                  >
                                    {isDefault ? '✓ Default' : 'Imposta Default'}
                                  </button>
                                )}
                              </div>
                            );
                          })}
                          {formData.tipologie_ids.length === 0 && (
                            <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded">
                              <p className="text-xs text-yellow-800 font-medium">
                                ⚠️ Seleziona almeno una tipologia usando le checkbox sopra
                              </p>
                            </div>
                          )}
                          {formData.tipologie_ids.length > 0 && (
                            <div className="mt-2 pt-2 border-t border-gray-200">
                              <p className="text-xs text-gray-600">
                                ✓ <strong>{formData.tipologie_ids.length}</strong> tipologia/e selezionata/e
                                {formData.tipologia_default_id && (
                                  <span className="ml-2 text-blue-600">• Default impostata</span>
                                )}
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Categoria
                      </label>
                      {categorie.length === 0 ? (
                        <div className="mt-1 border border-yellow-300 rounded-md p-3 bg-yellow-50">
                          <p className="text-sm text-yellow-800">
                            ⚠️ Nessuna categoria disponibile. Vai su <strong>Categorie & Tipologie</strong> per crearne una.
                          </p>
                        </div>
                      ) : (
                        <select
                          required
                          className="mt-1 input"
                          value={formData.categoria}
                          onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                        >
                          <option value="">Seleziona categoria</option>
                          {categorie.map(categoria => (
                            <option key={categoria} value={categoria}>
                              {categoria.replace('_', ' ').toUpperCase()}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Nota Default (per ordini)
                      </label>
                      <textarea
                        rows="2"
                        className="mt-1 input"
                        placeholder="Es: 'Sempre maturi', 'Extra piccante', ecc..."
                        value={formData.nota_default}
                        onChange={(e) => setFormData({ ...formData, nota_default: e.target.value })}
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Questa nota verrà inserita automaticamente quando aggiungi questo prodotto a un ordine. Può essere modificata nell'ordine.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={tipologie.length === 0 || categorie.length === 0}
                  >
                    {editingProdotto ? 'Aggiorna' : 'Crea'} Prodotto
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="mt-3 sm:mt-0 sm:mr-3 btn btn-outline"
                  >
                    Annulla
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modale di conferma eliminazione */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setProdottoToDelete(null);
        }}
        title="Conferma Eliminazione"
        message={`Sei sicuro di voler eliminare il prodotto "${prodottoToDelete?.nome}"?\n\nQuesta azione non può essere annullata.`}
        type="warning"
        confirmText="Sì, Elimina"
        cancelText="Annulla"
        onConfirm={executeDeleteProdotto}
      />
    </div>
  );
}

export default Prodotti;

